import React, { useState } from "react";
import { UsersList }         from "./UsersList";
import { AddUserForm }       from "./AddUserForm";
import { EditUserForm }      from "./EditUserForm";
import { PermissionsMatrix } from "./PermissionsMatrix";
import { RolesPage }         from "./RolesPage";
import SecurityDashboard     from "./SecurityDashboard";
import { AuditTrailPage }    from "./AuditTrailPage";

const NAV_BASE = [
  { key: "users",       label: "Users",               icon: "👥",  superAdminOnly: false },
  { key: "permissions", label: "Permissions Matrix",  icon: "🔒",  superAdminOnly: false },
  { key: "roles",       label: "Roles",               icon: "🏷️",  superAdminOnly: false },
  { key: "security",    label: "Security",             icon: "🛡️",  superAdminOnly: true  },
  { key: "audit",       label: "Audit Trail",          icon: "📋",  superAdminOnly: true  },
];

export function AdminSection({ user, theme: t, isMobile }) {
  const [page,           setPage]           = useState("users");
  const [editingUserId,  setEditingUserId]  = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const isSuperAdmin = user?.role === "super_admin";
  const NAV = NAV_BASE.filter(n => !n.superAdminOnly || isSuperAdmin);

  function navigateTo(key, userId = null) {
    setEditingUserId(userId);
    setPage(key);
  }

  function onUserSaved() {
    setListRefreshKey(k => k + 1);
    setPage("users");
  }

  // Breadcrumb label
  const breadcrumb = {
    users:       "Users",
    add:         "Add New User",
    edit:        "Edit User",
    permissions: "Permissions Matrix",
    roles:       "Roles",
    security:    "Security",
    audit:       "Audit Trail",
  }[page] || "Users";

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20, fontSize: 12, color: t.textMuted }}>
        <span style={{ color: t.textFaint }}>Admin</span>
        <span style={{ color: t.textFaint }}>›</span>
        {page !== "users" && page !== "permissions" && page !== "roles" && page !== "security" && page !== "audit" && (
          <>
            <button onClick={() => setPage("users")} style={{ background: "none", border: "none", padding: 0, color: "#2563EB", fontSize: 12, cursor: "pointer", fontFamily: "Georgia, serif" }}>Users</button>
            <span style={{ color: t.textFaint }}>›</span>
          </>
        )}
        <span style={{ color: t.text, fontWeight: 700 }}>{breadcrumb}</span>
      </div>

      {/* Sub-nav tabs */}
      {(page === "users" || page === "permissions" || page === "roles" || page === "security" || page === "audit") && (
        <div style={{ display: "flex", gap: 6, marginBottom: 24, borderBottom: `1px solid ${t.border}`, paddingBottom: 12 }}>
          {NAV.map(n => (
            <button key={n.key} onClick={() => setPage(n.key)}
              style={{
                background: page === n.key ? t.surface2 : "none",
                border: `1px solid ${page === n.key ? t.border2 : "transparent"}`,
                borderRadius: 8, padding: "6px 14px",
                color: page === n.key ? t.text : t.textMuted,
                fontSize: 12, cursor: "pointer", fontFamily: "Georgia, serif",
                fontWeight: page === n.key ? 800 : 500,
                display: "flex", alignItems: "center", gap: 5,
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { if (page !== n.key) e.currentTarget.style.background = t.surface2; }}
              onMouseLeave={e => { if (page !== n.key) e.currentTarget.style.background = "none"; }}>
              <span>{n.icon}</span>{n.label}
            </button>
          ))}
        </div>
      )}

      {/* Sub-pages */}
      {page === "users" && (
        <UsersList
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
          theme={t}
          isMobile={isMobile}
          refreshKey={listRefreshKey}
          onAdd={() => setPage("add")}
          onEdit={id => navigateTo("edit", id)}
        />
      )}
      {page === "add" && (
        <AddUserForm
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
          theme={t}
          onSaved={onUserSaved}
          onCancel={() => setPage("users")}
        />
      )}
      {page === "edit" && editingUserId && (
        <EditUserForm
          userId={editingUserId}
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
          theme={t}
          onSaved={onUserSaved}
          onCancel={() => setPage("users")}
        />
      )}
      {page === "permissions" && (
        <PermissionsMatrix
          currentUser={user}
          isSuperAdmin={isSuperAdmin}
          theme={t}
          isMobile={isMobile}
        />
      )}
      {page === "roles" && (
        <RolesPage theme={t} />
      )}
      {page === "security" && isSuperAdmin && (
        <SecurityDashboard theme={t} />
      )}
      {page === "audit" && isSuperAdmin && (
        <AuditTrailPage user={user} theme={t} isMobile={isMobile} />
      )}
    </div>
  );
}
